// File: js/db/persistence.js
//
// The storage durability gate (spec §7, first line of the collection gate).
//
// Unsynced cash lives in IndexedDB, which is *best-effort* storage the browser may
// evict under pressure — unless the origin has been granted PERSISTENT storage. A
// device that cannot durably hold a payment must not be allowed to accept one; it
// says « Cet appareil ne peut pas conserver les paiements en sécurité — utilisez le
// carnet papier aujourd'hui. » (spec §9) and the cashier writes on paper.
//
// FAIL-SAFE, not "probably fine": a missing Storage API, a Storage API that throws,
// or a `persist()` that returns anything other than `true` all count as NOT durable.
// The alternative — assuming durability because we could not check — is a silent
// bet with a patient's money.

import { getMeta, setMeta, META_KEYS } from './schema.js';

/**
 * Ask the browser for persistent storage once and record the answer in
 * `meta.persist_granted`.
 *
 * `navigator` is injected so tests can drive granted/refused/throwing/absent
 * without a real browser.
 *
 * @param {import('dexie').Dexie} db
 * @param {{navigator?: {storage?: {persist?: () => Promise<boolean>, persisted?: () => Promise<boolean>}}}} [deps]
 * @returns {Promise<boolean>} Whether persistence is granted.
 */
export async function requestPersist(db, deps = {}) {
    const nav = deps.navigator !== undefined
        ? deps.navigator
        : (typeof navigator !== 'undefined' ? navigator : undefined);

    let granted = false;
    try {
        if (nav && nav.storage) {
            // Already granted on a previous visit? `persisted()` answers without
            // re-prompting. Only a strict `true` counts.
            if (typeof nav.storage.persisted === 'function') {
                granted = (await nav.storage.persisted()) === true;
            }
            if (!granted && typeof nav.storage.persist === 'function') {
                granted = (await nav.storage.persist()) === true;
            }
        }
    } catch {
        // Absent or throwing Storage API ⇒ durability is unproven ⇒ not durable.
        granted = false;
    }

    await setMeta(db, META_KEYS.PERSIST_GRANTED, granted);
    return granted;
}

/**
 * Whether this device has proven durable storage.
 *
 * Absent meta → `false`. Never "assume yes": the whole point of the gate is that
 * an unproven device refuses cash rather than risk silently evicting it.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<boolean>}
 */
export async function isPersistGranted(db) {
    try {
        return (await getMeta(db, META_KEYS.PERSIST_GRANTED, false)) === true;
    } catch {
        // An unreadable meta store is not evidence of durability either.
        return false;
    }
}
